import toLower from 'lodash/toLower';
import isEqual from 'lodash/isEqual';

import { showNotification, TYPE_WARNING } from '../notifications/actions';
import {
    isInBundle,
    isInCampaigns,
    isInCart,
    isInCatalogue,
    isInCollectionByGenre,
    isInCollectionNewlyAdded,
    isInCompleteCollection,
    isInLanguageLearningFeatured,
    isInLanguageLearningNewlyAdded,
    isInLanguageLearningProducts,
    isInLibraryManagedProductGroup,
    isInLibraryManagedProductGroups,
    isInLoteEntry,
    isInLoteFeatured,
    isInLoteGenres,
    isInLoteNewlyAdded,
    isInLoteProducts,
    isInMarketingCampaigns,
    isInOrderAssistant,
    isInOrders,
    isInProducts,
    isInProductType,
    isInSelectionlist,
    isInShopSearch,
    isInYourCollection
} from '../../shared/utils/utils-viewhierarchy';
import { LOANFORMAT_ALL } from '../loanFormat/actions';
import { ALL_LANGUAGES } from '../../shop/language/constants';
import * as types from '../constants';
import { LineItemKey } from '../api';
import {
    DESELECT_ALL,
    DESELECT_MULTIPLE,
    DESELECT_ONE,
    DeselectAll,
    DeselectMultiple,
    DeselectOne,
    SELECT_ALL,
    SELECT_MULTIPLE,
    SELECT_ONE,
    SelectMulitple,
    SET_SELECTION_MODE,
    SetSelectionMode
} from './types';
import { AppDispatch, GetAppState } from '../index';
import { ProductSelectionState } from './state';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { LendingModel } from '../../../models/PriceTier';
import { isInYourBorrowBox } from '../search/actions';
import { ProductMetaData } from '../../../models/Product';
import queryString from 'query-string';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import i18n from '../../../i18n';

const MAX_SELECT = 10000;

export type ProductIdOrOptions =
    | string
    | {
          id: string;
          price?: {
              lendingModel?: LendingModel;
              termLimit?: number;
              usageLimit?: number;
          };
          subscriptionPeriodType?: SubscriptionPeriodType;
      };

type SourceParams = {
    source: string;
    sourceId?: string | null;
    genreId?: string | null;
};

export function selectAll(count = 0) {
    return (dispatch: AppDispatch) => {
        if (count > MAX_SELECT) {
            dispatch(showMaxCountWarning());
        } else {
            dispatch({
                type: SELECT_ALL,
                payload: count
            });
        }
    };
}

export function deselectAll(): DeselectAll {
    return {
        type: DESELECT_ALL
    };
}

export function selectOne(productIdOrOptions: ProductIdOrOptions) {
    const lineItemKey = mapToLineItemKey(productIdOrOptions);

    return (dispatch: AppDispatch, state: GetAppState) => {
        if (state().productSelection.count === MAX_SELECT) {
            dispatch(showMaxCountWarning());
        } else {
            dispatch({
                type: SELECT_ONE,
                payload: {
                    productId: lineItemKey
                }
            });
        }
    };
}

export function selectMultiple(lineItemKeys: Array<LineItemKey>): SelectMulitple {
    return {
        type: SELECT_MULTIPLE,
        payload: { productIds: lineItemKeys }
    };
}

export function deselectOne(productIdOrOptions: ProductIdOrOptions): DeselectOne {
    const lineItemKey = mapToLineItemKey(productIdOrOptions);

    return {
        type: DESELECT_ONE,
        payload: {
            productId: lineItemKey
        }
    };
}

export function deselectMultiple(productIds: Array<string> = []): DeselectMultiple {
    const lineItemKeys = productIds.map(productIdOrOptions => mapToLineItemKey(productIdOrOptions));

    return {
        type: DESELECT_MULTIPLE,
        payload: { productIds: lineItemKeys }
    };
}

export function mapToLineItemKey(productIdOrOptions: ProductIdOrOptions): LineItemKey {
    const {
        id: productId,
        price = undefined,
        subscriptionPeriodType = undefined
    } = typeof productIdOrOptions === 'string' ? { id: productIdOrOptions } : productIdOrOptions;

    const usageCondition = price
        ? {
              lendingModel: price.lendingModel,
              usageLimit: price.usageLimit,
              termLimit: price.termLimit
          }
        : undefined;

    return { productId, usageCondition, subscriptionPeriodType };
}

export function setSelectionMode(state: boolean): SetSelectionMode | DeselectAll {
    if (state) {
        return {
            type: SET_SELECTION_MODE,
            payload: state
        };
    } else {
        // if state is false reset whole product selection
        // which in turn also sets selectionMode to false
        return deselectAll();
    }
}

export function isSelected(
    productOrId: string | (ProductMetaData & Article),
    productSelection: ProductSelectionState
): boolean {
    const lineItemKey = mapToLineItemKey(productOrId);

    if (productSelection.all) {
        return !productSelection.excluded?.find(item => isEqual(item, lineItemKey));
    } else {
        return !!productSelection.selected?.find(item => isEqual(item, lineItemKey));
    }
}

function showMaxCountWarning() {
    return showNotification(
        i18n.t('notifications.showMaxCountWarning.warning', {
            maxSelect: MAX_SELECT
        }),
        TYPE_WARNING
    );
}

export function nothingSelected(productSelection: ProductSelectionState) {
    return !productSelection.all && !productSelection.selected?.length;
}

export function isInSelectionMode(productSelection?: ProductSelectionState | null) {
    if (!productSelection) return false;
    return productSelection.selectionMode || !nothingSelected(productSelection);
}

export function buildBulkRequestPathParamsAndData(
    action: string,
    amount: number,
    getState: GetAppState,
    isFilteredFromBundle = false
) {
    const productSelection = getState().productSelection;
    if (!isFilteredFromBundle && nothingSelected(productSelection)) {
        throw new Error(i18n.t('notifications.buildBulkRequestPathParamsAndData.error'));
    }

    const sourceParams = determineSourceParams(getState);
    const pathParams = {
        source: sourceParams.source,
        kind: '',
        action,
        listId: null
    };

    const search = getState().search;
    const loanFormat =
        pathParams.source === types.SOURCE_CATALOGUE || types.SOURCE_COLLECTION_BY_GENRE
            ? search.query && search.loanFormat
                ? search.loanFormat.key
                : getState().loanFormat.key
            : null;

    const inYourBorrowBox = isInYourBorrowBox(getState());
    const { q: fallbackSearchQuery, scope: scopeQueryParam } = queryString.parse(window.location.search);

    const data = {
        amount,
        loanFormat,
        source: sourceParams.source,
        sourceId: sourceParams.sourceId,
        query: getState().search.query || getState().search.q || fallbackSearchQuery,
        genreId: sourceParams.genreId,
        catalogueSearchScope: !inYourBorrowBox
            ? scopeQueryParam && typeof scopeQueryParam === 'string'
                ? scopeQueryParam.toUpperCase()
                : undefined
            : undefined,
        collectionSearchScope: inYourBorrowBox ? getState().search.scope?.requestKey : undefined,
        ...determineGrouping(getState)
    };

    const filterQueryParam = queryString.parse(window.location.search).filter;

    if (productSelection.all || isFilteredFromBundle) {
        pathParams.kind = 'filter';
        data['filterRequest'] = deserializeFilter(filterQueryParam as string);
        data['excludedLineItemKeys'] = getState().productSelection.excluded;
    } else {
        pathParams.kind = 'ids';
        data['lineItemKeys'] = getState().productSelection.selected;
    }

    if (isEqual(toLower(data.loanFormat), toLower(LOANFORMAT_ALL))) {
        delete (data as any).loanFormat;
    }

    return { pathParams, data };
}

export function determineSourceParams(state: GetAppState) {
    const params: SourceParams = {
        source: determineBulkSource(state)
    };

    if (params.source === types.SOURCE_SELECTIONLIST) {
        params.sourceId = state().orderlist.id;
    } else if (params.source === types.SOURCE_BUNDLE) {
        params.sourceId = state().bundle.bundleId;
    } else if (params.source === types.SOURCE_COLLECTION_BY_GENRE) {
        params.sourceId = state().collection.byGenre;
    } else if (params.source === types.SOURCE_PRODUCT_GROUPS) {
        const pathname = window.location.pathname;
        params.sourceId = pathname.substring(pathname.lastIndexOf('/') + 1);
    } else if (
        [
            types.SOURCE_LOTE_PRODUCTS,
            types.SOURCE_LOTE_NEWLY_ADDED,
            types.SOURCE_LOTE_GENRE,
            types.SOURCE_LANGUAGE_LEARNING_PRODUCTS,
            types.SOURCE_LANGUAGE_LEARNING_NEWLY_ADDED
        ].includes(params.source)
    ) {
        const selectedLanguage = state().foreignLanguage.selectedLanguage;
        params.sourceId = !isEqual(toLower(selectedLanguage), toLower(ALL_LANGUAGES)) ? selectedLanguage : undefined;

        if (params.source === types.SOURCE_LOTE_GENRE) {
            params.genreId = state().foreignLanguage.lote.selectedGenre;
        }
    }

    return params;
}

export function determineGrouping(state: GetAppState) {
    const group = state().grouping;

    const source = determineBulkSource(state);

    if (source === types.SOURCE_CART || source === types.SOURCE_SELECTIONLIST) {
        if (null !== group.id) {
            if (group.key === types.GROUPBY_BUNDLES) {
                return { bundleRef: group.id };
            } else if (group.key === types.GROUPBY_LISTS) {
                return { selectionListRef: group.id };
            }
        }
    }
}

export function determineCurrentPage(state: GetAppState): string {
    return determineBulkSource(state);
}

function determineBulkSource(getState: GetAppState): string {
    let source = ''; // FIXME What should happen if it is empty (previously null)
    const state = getState();

    if (
        isInCatalogue(state) ||
        isInProductType(state) ||
        isInShopSearch(state) ||
        isInProducts(state) ||
        isInOrders(state) ||
        isInOrderAssistant(state) ||
        isInLoteFeatured(state) ||
        isInLoteEntry(state) ||
        isInLanguageLearningFeatured(state)
    ) {
        source = types.SOURCE_CATALOGUE;
    } else if (isInBundle(state)) {
        source = types.SOURCE_BUNDLE;
    } else if (isInCart(state)) {
        source = types.SOURCE_CART;
    } else if (isInSelectionlist(state)) {
        source = types.SOURCE_SELECTIONLIST;
    } else if (isInCollectionByGenre(state)) {
        source = types.SOURCE_COLLECTION_BY_GENRE;
    } else if (isInCollectionNewlyAdded(state)) {
        source = types.SOURCE_COLLECTION_NEWLY_ADDED;
    } else if (isInCompleteCollection(state)) {
        source = types.SOURCE_COLLECTION;
    } else if (isInYourCollection(state)) {
        source = types.SOURCE_COLLECTION;
    } else if (isInLoteProducts(state)) {
        source = types.SOURCE_LOTE_PRODUCTS;
    } else if (isInLoteNewlyAdded(state)) {
        source = types.SOURCE_LOTE_NEWLY_ADDED;
    } else if (isInLoteGenres(state)) {
        source = types.SOURCE_LOTE_GENRE;
    } else if (isInLanguageLearningProducts(state)) {
        source = types.SOURCE_LANGUAGE_LEARNING_PRODUCTS;
    } else if (isInLanguageLearningNewlyAdded(state)) {
        source = types.SOURCE_LANGUAGE_LEARNING_NEWLY_ADDED;
    } else if (isInCampaigns(state) || isInMarketingCampaigns(state)) {
        source = types.SOURCE_CAMPAIGN_PRODUCTS;
    } else if (isInLibraryManagedProductGroup(state) || isInLibraryManagedProductGroups(state)) {
        source = types.SOURCE_PRODUCT_GROUPS;
    }

    return source;
}
